import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from '../../icons';
import { surfaces, borders, textColors, typography, brandColors } from '../../tokens';

/**
 * Modal Component
 * 
 * A fully accessible modal dialog with animations, focus trap, and overlay.
 * Renders using React Portal for proper z-index layering.
 * 
 * Features:
 * - Focus trap (keyboard navigation contained within modal)
 * - ESC key to close
 * - Click overlay to close (optional)
 * - Body scroll lock when open
 * - Smooth animations
 * - Accessible (ARIA attributes, focus management)
 * 
 * @example
 * // Basic modal
 * const [isOpen, setIsOpen] = useState(false);
 * 
 * <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} title="Modal Title">
 *   <p>Modal content</p>
 * </Modal>
 * 
 * @example
 * // Modal with footer actions
 * <Modal
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   title="Confirm Action"
 *   footer={
 *     <>
 *       <SecondaryButton onClick={handleClose}>Cancel</SecondaryButton>
 *       <PrimaryButton onClick={handleConfirm}>Confirm</PrimaryButton>
 *     </>
 *   }
 * >
 *   <p>Are you sure you want to continue?</p>
 * </Modal>
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  titleAlign = 'left',
  children,
  footer,
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEsc = true,
  showCloseButton = true,
  className = '',
  bodyMaxHeight,
  ...props
}) => {
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);
  const hasBuiltInHeader = Boolean(title || showCloseButton);
  
  // Size classes
  const sizeClasses = {
    sm: 'max-w-md',      // 448px
    md: 'max-w-lg',      // 512px
    lg: 'max-w-2xl',     // 672px
    xl: 'max-w-4xl',     // 896px
    full: 'max-w-full mx-4',
  };
  
  // Focus trap: Find all focusable elements
  const getFocusableElements = useCallback(() => {
    if (!modalRef.current) return [];
    
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');
    
    return Array.from(modalRef.current.querySelectorAll(focusableSelectors));
  }, []);
  
  // Handle focus trap
  const handleTabKey = useCallback((e) => {
    const focusableElements = getFocusableElements();
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    if (e.shiftKey) {
      // Shift + Tab: Focus previous element
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab: Focus next element
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }, [getFocusableElements]);
  
  // Handle ESC key
  const handleEscKey = useCallback((e) => {
    if (e.key === 'Escape' && closeOnEsc) {
      onClose();
    }
  }, [closeOnEsc, onClose]);
  
  // Keyboard event handler
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Tab') {
      handleTabKey(e);
    } else if (e.key === 'Escape') {
      handleEscKey(e);
    }
  }, [handleTabKey, handleEscKey]);
  
  // Effect: Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save currently focused element
      previousActiveElement.current = document.activeElement;
      
      // Lock body scroll
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      
      // Focus first focusable element in modal
      setTimeout(() => {
        const focusableElements = getFocusableElements();
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }, 100);
      
      // Cleanup
      return () => {
        document.body.style.overflow = originalOverflow;
        
        // Restore focus to previously focused element
        if (previousActiveElement.current && previousActiveElement.current.focus) {
          previousActiveElement.current.focus();
        }
      };
    }
  }, [isOpen, getFocusableElements]);
  
  // Handle overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && closeOnOverlayClick) {
      onClose();
    }
  };
  
  // Don't render anything if modal is not open
  if (!isOpen) return null;
  
  // Modal content
  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-[5100] flex items-center justify-center"
          onKeyDown={handleKeyDown}
        >
          {/* Overlay/Backdrop */}
          <motion.div
            className="fixed inset-0 bg-dark-950/50 dark:bg-dark-950/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleOverlayClick}
            aria-hidden="true"
          />
          
          {/* Modal Container */}
          <motion.div
            ref={modalRef}
            className={`relative w-full ${sizeClasses[size]} bg-white dark:bg-dark-900/80 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border ${borders.glass} ${className}`}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? 'modal-title' : undefined}
            {...props}
          >
            {/* Header */}
            {(title || showCloseButton) && (
              <div className={`flex items-center justify-between px-6 py-4 rounded-t-3xl ${brandColors.primaryBg}`}>
                {titleAlign === 'center' && showCloseButton ? <div className="w-8" aria-hidden="true" /> : null}
                {title && (
                  <h2
                    id="modal-title"
                    className={`${typography.h4Dark} ${titleAlign === 'center' ? 'flex-1 text-center' : ''}`}
                  >
                    {title}
                  </h2>
                )}
                {showCloseButton && (
                  <button
                    onClick={onClose}
                    className="text-white/90 hover:text-white transition-colors p-1 rounded-md hover:bg-white/10"
                    aria-label="Close modal"
                  >
                    <XIcon size="md" />
                  </button>
                )}
              </div>
            )}
            
            {/* Body */}
            <div
              className={`px-6 overflow-y-auto ${hasBuiltInHeader ? 'pb-5 pt-4' : 'py-5'}`}
              style={{ maxHeight: bodyMaxHeight || 'calc(100vh - 200px)' }}
            >
              {children}
            </div>
            
            {/* Footer */}
            {footer && (
              <div className={`px-6 py-4 flex items-center justify-end space-x-3 border-t ${borders.glass}`}>
                {footer}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
  
  // Render using portal
  return createPortal(modalContent, document.body);
};

Modal.propTypes = {
  /** Whether modal is open */
  isOpen: PropTypes.bool.isRequired,
  /** Function to call when modal should close */
  onClose: PropTypes.func.isRequired,
  /** Modal title (shown in header) */
  title: PropTypes.string,
  /** Title alignment within the header */
  titleAlign: PropTypes.oneOf(['left', 'center']),
  /** Modal content */
  children: PropTypes.node.isRequired,
  /** Footer content (typically action buttons) */
  footer: PropTypes.node,
  /** Size variant */
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', 'full']),
  /** Whether clicking overlay closes modal */
  closeOnOverlayClick: PropTypes.bool,
  /** Whether ESC key closes modal */
  closeOnEsc: PropTypes.bool,
  /** Whether to show X close button in header */
  showCloseButton: PropTypes.bool,
  /** Additional CSS classes */
  className: PropTypes.string,
  /** Max height for the scrollable body area (CSS value, e.g. 'calc(100vh - 120px)') */
  bodyMaxHeight: PropTypes.string,
};

/**
 * ModalHeader Component
 * 
 * Custom header for modal when you need more control than the default title.
 * 
 * @example
 * <Modal isOpen={isOpen} onClose={onClose} showCloseButton={false}>
 *   <ModalHeader>
 *     <h2>Custom Header</h2>
 *     <button onClick={onClose}>Close</button>
 *   </ModalHeader>
 *   <ModalBody>Content</ModalBody>
 * </Modal>
 */
export const ModalHeader = ({ children, className = '', ...props }) => {
  return (
    <div
      className={`px-6 py-4 border-b ${borders.glass} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

ModalHeader.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

/**
 * ModalBody Component
 * 
 * Custom body for modal when you need more control over padding/scrolling.
 * 
 * @example
 * <Modal isOpen={isOpen} onClose={onClose} title="Title">
 *   <ModalBody className="p-0">
 *     <div className="p-6">Custom content with custom padding</div>
 *   </ModalBody>
 * </Modal>
 */
export const ModalBody = ({ children, className = '', ...props }) => {
  return (
    <div className={`px-6 py-5 ${className}`} {...props}>
      {children}
    </div>
  );
};

ModalBody.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

/**
 * ModalFooter Component
 * 
 * Custom footer for modal.
 * 
 * @example
 * <Modal isOpen={isOpen} onClose={onClose} title="Title">
 *   <ModalBody>Content</ModalBody>
 *   <ModalFooter>
 *     <button>Cancel</button>
 *     <button>Confirm</button>
 *   </ModalFooter>
 * </Modal>
 */
export const ModalFooter = ({ children, align = 'right', className = '', ...props }) => {
  const alignClasses = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
    between: 'justify-between',
  };
  
  return (
    <div
      className={`px-6 py-4 flex items-center ${alignClasses[align]} space-x-3 border-t ${borders.glass} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

ModalFooter.propTypes = {
  children: PropTypes.node.isRequired,
  align: PropTypes.oneOf(['left', 'center', 'right', 'between']),
  className: PropTypes.string,
};

export default Modal;
